import React, { useState, Suspense, FC, useEffect, useMemo } from 'react';
import { find, includes, map } from 'lodash';
import { WeldDetailTabStyled, TabButtonStyled } from './styled';
import { WeldDetailTab, WeldDetailTabsProps, WeldDetailTabType } from './types';
import { useLingui } from '@lingui/react';
import { ErrorBoundary, useEnvProvider, ProjectAppTypes } from '@bechtel/client-common';
import { Spinner, SpinnerSize } from '@bechtel/gds';
import { t } from '@lingui/macro';
import { WeldDetailSpecification } from './weld-detail-specs';
import { WeldActivityHistory } from './history';
import { CommodityStatusCode, MilestoneStatusCode } from '@bechtel/types';
import { useWorkPackage } from '~/detail/context';
import { useWorkPackagePermissions } from '~/hooks';
import { WeldAssignReassign } from '~/components/organisms/assign-reassign-weld';
import { FitupMilestoneMock } from '../../types';

export const WeldDetailTabs: FC<WeldDetailTabsProps> = ({ weld, isFitupRequired, showItrCertDialog, close }) => {
  const { assignedCrewMembers } = useWorkPackage();
  const { canCreateFitupRequest } = useWorkPackagePermissions(assignedCrewMembers);
  const [selectedTab, setSelectedTab] = useState<WeldDetailTabType>(
    canCreateFitupRequest ? WeldDetailTabType.ASSIGN : WeldDetailTabType.DETAILS
  );
  const { i18n } = useLingui();
  const { envConfig } = useEnvProvider();
  const fitupMilestone = useMemo(() => {
    return weld?.milestones?.filter(
      (milestone) =>
        includes(
          [MilestoneStatusCode.REQUESTED, MilestoneStatusCode.REJECTED, MilestoneStatusCode.NOT_APPLICABLE],
          milestone?.currentStatus?.codeI18n
        ) &&
        includes([CommodityStatusCode.FITUP_REQUESTED, CommodityStatusCode.SENT_BACK_TO_PIPING], weld?.status?.status)
    );
  }, [weld]);

  const installMilestone = useMemo(() => {
    return weld?.milestones?.filter(
      (milestone) =>
        includes(
          [MilestoneStatusCode.UNPLANNED, MilestoneStatusCode.REJECTED, MilestoneStatusCode.PLANNED],
          milestone?.currentStatus?.codeI18n
        ) && includes([CommodityStatusCode.READY_TO_WELD, CommodityStatusCode.VT_SENT_BACK], weld?.status?.status)
    );
  }, [weld]);

  const isAssignVisible = () => {
    if (fitupMilestone?.length == 0 && installMilestone?.length == 0) false;
    else if (fitupMilestone?.length > 0 || installMilestone?.length > 0) {
      if (weld?.fitupRequest?.installationCrewMember?.id) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  };

  const isReassignVisible = () => {
    if (
      (fitupMilestone.length > 0 && envConfig.appName === ProjectAppTypes.AS) ||
      (fitupMilestone.length == 0 && envConfig.appName === ProjectAppTypes.PF)
    )
      return false;
    if (fitupMilestone?.length == 0 && installMilestone?.length == 0) false;
    else if (fitupMilestone?.length > 0 || installMilestone?.length > 0) {
      if (weld?.fitupRequest?.installationCrewMember?.id) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (isAssignVisible() && envConfig.appName === ProjectAppTypes.PF && canCreateFitupRequest) {
      setSelectedTab(WeldDetailTabType.ASSIGN);
    } else if (isReassignVisible() && envConfig.appName === ProjectAppTypes.PF && canCreateFitupRequest) {
      setSelectedTab(WeldDetailTabType.REASSIGN);
    } else {
      setSelectedTab(WeldDetailTabType.DETAILS);
    }
  }, [fitupMilestone]);

  const fitupMilestoneDetail = find(weld?.milestones, (item) => {
    return item.name.toUpperCase() === FitupMilestoneMock.name.toUpperCase();
  });
  const isFitupApproved =
    fitupMilestoneDetail && fitupMilestoneDetail?.currentStatus?.codeI18n === MilestoneStatusCode.APPROVED;
  const WeldDetailTabList: WeldDetailTab[] = [
    {
      label: t`Assign`,
      value: WeldDetailTabType.ASSIGN,
      visible:
        isAssignVisible() && canCreateFitupRequest && envConfig.appName === ProjectAppTypes.PF && !isFitupApproved,
    },
    {
      label: t`Reassign`,
      value: WeldDetailTabType.REASSIGN,
      visible:
        isReassignVisible() && canCreateFitupRequest && envConfig.appName === ProjectAppTypes.PF && !isFitupApproved,
    },
    { label: t`Details`, value: WeldDetailTabType.DETAILS, visible: true },
    { label: t`History`, value: WeldDetailTabType.HISTORY, visible: true },
  ];
  useEffect(() => {
    if (isFitupApproved) {
      setSelectedTab(WeldDetailTabType.DETAILS);
    }
  }, [isFitupApproved]);

  return (
    <>
      <WeldDetailTabStyled>
        {map(WeldDetailTabList, (tabItem: WeldDetailTab) => {
          return (
            tabItem.visible && (
              <TabButtonStyled
                selected={tabItem.value === selectedTab}
                onClick={() => {
                  setSelectedTab(tabItem.value);
                }}
                key={tabItem.value}
              >
                {i18n._(tabItem.label)}
              </TabButtonStyled>
            )
          );
        })}
      </WeldDetailTabStyled>
      {(selectedTab == WeldDetailTabType.ASSIGN || selectedTab == WeldDetailTabType.REASSIGN) && (
        <ErrorBoundary>
          <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
            <WeldAssignReassign
              weldIds={weld?.id ? [weld.id] : []}
              showItrCertDialog={showItrCertDialog}
              isFitupRequired={isFitupRequired}
              close={close}
              isReassign={selectedTab === WeldDetailTabType.REASSIGN}
              fitupRequest={weld?.fitupRequest}
              weld={weld}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {selectedTab == WeldDetailTabType.DETAILS && (
        <ErrorBoundary>
          <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
            <WeldDetailSpecification weld={weld} />
          </Suspense>
        </ErrorBoundary>
      )}
      {selectedTab == WeldDetailTabType.HISTORY && (
        <ErrorBoundary>
          <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
            <WeldActivityHistory weld={weld} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
